import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ENDPOINT } from '@siloreal:app:config/endpoint';
import { getAuthSession, refreshAuthSession } from './auth-session';

export type FetchConfig = {
  query?: string;
  body?: unknown;
  timeout?: number;
  signal?: AbortSignal;
  // data?: unknown;
  // params?: unknown;
  // responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  // timeout?: number;
  // signal?: AbortSignal;
};

export type FetchAbstraction = {
  get: <TData = unknown>(url: string, config?: FetchConfig) => Promise<AxiosResponse<TData>>;
  post: <TData = unknown>(url: string, config?: FetchConfig) => Promise<AxiosResponse<TData>>;
  put: <TData = unknown>(url: string, config?: FetchConfig) => Promise<AxiosResponse<TData>>;
  patch: <TData = unknown>(url: string, config?: FetchConfig) => Promise<AxiosResponse<TData>>;
  delete: <TData = unknown>(url: string, config?: FetchConfig) => Promise<AxiosResponse<TData>>;
  blob: {
    get: (url: string, config?: FetchConfig) => Promise<AxiosResponse<Blob>>;
    post: (url: string, config?: FetchConfig) => Promise<AxiosResponse<Blob>>;
    put: (url: string, config?: FetchConfig) => Promise<AxiosResponse<Blob>>;
    patch: (url: string, config?: FetchConfig) => Promise<AxiosResponse<Blob>>;
    delete: (url: string, config?: FetchConfig) => Promise<AxiosResponse<Blob>>;
  };
  multipart: {
    post: (url: string, config: FetchConfig & { body: FormData }) => Promise<AxiosResponse<Blob>>;
  };
};

type AuthenticatedRequestConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
};

const TOKEN_EXPIRATION_LEEWAY_MS = 30_000;

function isAuthRequest(url?: string): boolean {
  return url?.split('?')[0] === ENDPOINT.LOGIN;
}

function shouldRefreshToken(accessToken: string | undefined, refreshToken: string | undefined, expiration: number) {
  if (refreshToken && !accessToken) return true;
  if (!accessToken) return false;

  return expiration <= Date.now() + TOKEN_EXPIRATION_LEEWAY_MS;
}

function setAuthorization(config: InternalAxiosRequestConfig, accessToken: string): void {
  config.headers.Authorization = `Bearer ${accessToken}`;
}

const createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_IOF_API,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: false, // Equivalente a mode: 'cors'
  });

  // Agrega un token vigente antes de ejecutar cualquier request autenticado.
  instance.interceptors.request.use(
    async (config: AuthenticatedRequestConfig) => {
      if (isAuthRequest(config.url)) return config;

      const session = getAuthSession();
      let { accessToken } = session;

      if (shouldRefreshToken(accessToken, session.refreshToken, session.expiration)) {
        accessToken = await refreshAuthSession();
        config._authRetry = true;
      }

      if (accessToken) setAuthorization(config, accessToken);

      return config;
    },
    (error: unknown) => {
      return Promise.reject(error);
    }
  );

  // Interceptor para logging (similar a fetch-backend)
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      /* eslint-disable no-console */
      console.groupCollapsed('[fetch]', response.config.method?.toUpperCase(), response.config.url);
      console.log('[fetch] [PARAMS]', response.config.params);
      console.log('[fetch] [BODY]', response.config.data);
      console.log('------------------------------------');
      console.log('[fetch] [RESPONSE]', response);
      console.log('[fetch] [DATA]', response.data);
      console.groupEnd();
      /* eslint-enable no-console */

      return response;
    },
    async (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 401 && error.config) {
        const config = error.config as AuthenticatedRequestConfig;

        if (!config._authRetry && !isAuthRequest(config.url)) {
          config._authRetry = true;

          try {
            const accessToken = await refreshAuthSession();
            setAuthorization(config, accessToken);
            return instance.request(config);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
      }

      /* eslint-disable no-console */
      if (axios.isAxiosError(error)) {
        console.groupCollapsed('[fetch] ERROR', error.config?.method?.toUpperCase(), error.config?.url);
        console.log('[fetch] [PARAMS]', error.config?.params);
        console.log('[fetch] [BODY]', error.config?.data);
        console.log('------------------------------------');
        console.log('[fetch] [ERROR]', error);
        console.log('[fetch] [RESPONSE]', error.response);
        console.groupEnd();
      } else {
        console.error('[fetch] Unexpected error:', error);
      }
      /* eslint-enable no-console */

      return Promise.reject(error);
    }
  );

  return instance;
};

const axiosInstance = createAxiosInstance();

export const fetch: FetchAbstraction = {
  get: async <TData = unknown>(url: string, config?: FetchConfig) =>
    axiosInstance.get<TData>(url + (config?.query ?? ''), config),
  post: async <TData = unknown>(url: string, config?: FetchConfig) =>
    axiosInstance.post<TData>(url + (config?.query ?? ''), config?.body, config),
  put: async <TData = unknown>(url: string, config?: FetchConfig) =>
    axiosInstance.put<TData>(url + (config?.query ?? ''), config?.body, config),
  patch: async <TData = unknown>(url: string, config?: FetchConfig) =>
    axiosInstance.patch<TData>(url + (config?.query ?? ''), config?.body, config),
  delete: async <TData = unknown>(url: string, config?: FetchConfig) =>
    axiosInstance.delete<TData>(url + (config?.query ?? ''), config),
  blob: {
    get: async (url: string, config?: FetchConfig) =>
      axiosInstance.get<Blob>(url + (config?.query ?? ''), { ...config, responseType: 'blob' }),
    post: async (url: string, config?: FetchConfig) =>
      axiosInstance.post<Blob>(url + (config?.query ?? ''), config?.body, {
        ...config,
        responseType: 'blob',
      }),
    put: async (url: string, config?: FetchConfig) =>
      axiosInstance.put<Blob>(url + (config?.query ?? ''), config?.body, { ...config, responseType: 'blob' }),
    patch: async (url: string, config?: FetchConfig) =>
      axiosInstance.patch<Blob>(url + (config?.query ?? ''), config?.body, {
        ...config,
        responseType: 'blob',
      }),
    delete: async (url: string, config?: FetchConfig) =>
      axiosInstance.delete<Blob>(url + (config?.query ?? ''), { ...config, responseType: 'blob' }),
  },
  multipart: {
    post: async (url: string, config: FetchConfig & { body: FormData }) => {
      const { body, query = '', ...axiosConfig } = config;

      return axiosInstance.postForm<Blob>(url + query, body, {
        ...axiosConfig,
        responseType: 'blob',
      });
    },
  },
};
